import { FiLoader, FiSearch, FiShield, FiZap } from "react-icons/fi";
import { restrictiveAnalysisTokens } from "../tokens";

interface TopFilterBarProps {
  documentValue: string;
  loading: boolean;
  onDocumentValueChange: (value: string) => void;
  onSubmit: () => void;
}

export function TopFilterBar({
  documentValue,
  loading,
  onDocumentValueChange,
  onSubmit,
}: TopFilterBarProps) {
  const {
    brandBlue,
    brandOrange,
    brandOrangeHover,
    brandOrangeDisabled,
  } = restrictiveAnalysisTokens.colors;

  
  const digits = documentValue.replace(/\D/g, "");
  const isValid11Digits = digits.length === 11;
  const isValid14Digits = digits.length === 14;
  const placeholder = isValid14Digits ? "00.000.000/0000-00 (CNPJ)" : "000.000.000-00 (CPF)";

  return (
    <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="grid grid-cols-1 lg:grid-cols-12">
        <div className="relative overflow-hidden border-b border-slate-200 p-4 lg:col-span-4 lg:border-b-0 lg:border-r lg:p-5">
          <div
            className="pointer-events-none absolute -right-16 -top-12 h-40 w-40 rounded-full"
            style={{ background: `${brandOrange}08` }}
          />
          <div
            className="pointer-events-none absolute -bottom-20 -left-16 h-44 w-44 rounded-full"
            style={{ background: `${brandOrange}08` }}
          />

          <div className="relative space-y-3">
            <span
              className="inline-flex items-center gap-2 rounded-full border px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
              style={{ color: brandBlue, borderColor: `${brandBlue}33` }}
            >
              <FiShield className="h-3.5 w-3.5" />
              Consulta Segura
            </span>

            <div>
              <h3 className="text-base font-semibold" style={{ color: brandBlue }}>
                Validação em tempo real
              </h3>
              <p className="mt-1 text-xs text-slate-600">
                Informe um documento para iniciar a análise restritiva com detecção automática de tipo.
              </p>
            </div>

            <div className="inline-flex items-center gap-2 rounded-xl bg-slate-100 px-3 py-1.5 text-xs text-slate-600">
              <FiZap className="h-3.5 w-3.5" style={{ color: brandOrange }} />
              CPF e CNPJ no mesmo fluxo
            </div>
          </div>
        </div>

        <div className="p-4 lg:col-span-8 lg:p-5">
          <div className="space-y-2">
            <label htmlFor="document-number" className="block text-sm font-semibold" style={{ color: brandBlue }}>
              CPF ou CNPJ
            </label>

            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="relative min-w-0 flex-1">
                <FiSearch
                  className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2"
                  style={{ color: brandBlue }}
                />
                <input
                  id="document-number"
                  value={documentValue}
                  onChange={(event) => onDocumentValueChange(event.target.value)}
                  placeholder={placeholder}
                  className="h-10 w-full rounded-xl border border-slate-300 bg-slate-50 pl-11 pr-4 text-sm text-slate-700 outline-none transition"
                  disabled={loading}
                  maxLength={18}
                />
              </div>

              <div className="w-full md:w-[220px] md:shrink-0">
                <button
                  type="button"
                  disabled={loading || (digits.length !== 11 && digits.length !== 14)}
                  onClick={onSubmit}
                  className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 disabled:cursor-not-allowed"
                  style={{ backgroundColor: loading ? brandOrangeDisabled : brandOrange }}
                >
                  {loading ? <FiLoader className="h-4 w-4 animate-spin" /> : <FiSearch className="h-4 w-4" />}
                  {loading ? "Consultando..." : "Consultar"}
                </button>
              </div>
            </div>

            <p className="text-xs text-slate-500">
              O formato é aplicado automaticamente conforme a quantidade de dígitos.
            </p>
          </div>
        </div>
      </div>

      <style>{`
        #document-number:focus {
          border-color: ${brandOrange};
          box-shadow: 0 0 0 2px ${brandOrange}33;
        }
        #document-number:hover {
          border-color: ${brandOrangeHover};
        }
      `}</style>
    </section>
  );
}
